import {
  type ButtonHTMLAttributes,
  Fragment,
  type MouseEvent,
  type ReactNode,
  type Ref,
  useState,
} from 'react';
import { Link } from 'react-router';
import { PaneHost } from '../../app/shell/PaneHost';
import { Button } from '../Button/Button';
import { cx } from '../cx';
import { Icon } from '../Icon/Icon';
import { MOTION, type MotionState, useMotion } from '../motion';

/**
 * THE SETTINGS PANE IS ONE CONSTRUCTION, AND THIS FILE IS IT (P7-1, brief
 * `CRM-REDESIGN-BRIEF-P7-SETTINGS-PANE-2026-09-10.md`, spec
 * `crm-design/spec/screens/settings-pane.md` section 2).
 *
 * A pane is a column of GROUP BOXES. A group box is a 3 percent ink wash with a 12px
 * radius over the pane - the macOS 27 kit's `Group Boxes/{Light,Dark}` master - and NOT a
 * white card with a border and a shadow. That difference is the whole point of the slice:
 * `Card` paints a raised data surface, and a settings pane painted out of raised data
 * surfaces reads as a dashboard. The first group carries no title; every later group gets a
 * bold title ABOVE the box; help text is one grey sentence UNDER the box, never inside it
 * and never in a box of its own (spec section 7, "what Apple does not do here").
 *
 * A ROW IS LABEL LEADING, CONTROL TRAILING. Apple never left-aligns a control under its
 * label in a settings row, so `SettingsRow` has one shape and the caller fills two slots.
 * Rows are separated by hairlines, not by gaps: the box is one object.
 *
 * NO ICON-BUTTON ROWS. Up, down and trash as bare glyphs at the end of a row are a web
 * table's vocabulary; the row carries ONE trailing More button and every verb lives in its
 * menu (P7-1). That is also what makes the row work on a phone, where three 24px targets in
 * a row do not.
 *
 * THE PHONE IS THE SAME GROUPS AS AN INSET GROUPED LIST (P7-2): below the desktop
 * breakpoint the box takes the iOS 27 kit's `Lists/{Light,Dark}/Grouped Table View`
 * geometry - opaque fill, 26px radius, 16px row insets - and the rows keep their order and
 * their labels. One construction, two geometries, no second component.
 */

/**
 * THE PANE'S GROUND AND ITS INSET (round 2 F3; wave 3 S-C1).
 *
 * A settings pane is not "whatever the route renders": it is a surface with a GROUND and an
 * INSET, and both are Apple's. The ground is the kit's window background rather than the
 * product canvas, because a 3 percent wash is only as visible as what it falls on and the
 * canvas is near-black in the dark, where the wash disappeared.
 *
 * THE 460px COLUMN IS GONE (wave 3, S-C1). Round 2 read the kit's Example Forms master as a
 * WIDTH - a 460 form - and gave the pane that column. The same master's other half is a 500pt
 * WINDOW around it, and the Mac sizes a settings window to its pane; a browser window does
 * not, so the column left two thirds of a 1440 window empty and the owner said so on
 * 2026-09-10. What the kit gives a fixed pane is therefore its INSET, not its width: iPad
 * Settings and a System Settings pane both run their groups from inset to inset with the
 * label leading and the control trailing. `--settings-pane-inset` is that 20pt, and it is
 * the pane's own padding, so the toolbar row and the group boxes stand on ONE leading edge.
 *
 * IT WRAPS THE WHOLE ROUTE, TOOLBAR INCLUDED. The ground is full bleed (index.css cancels
 * `<main>`'s gutter and pays back the kit's inset instead), so the pane is one surface from
 * the top of the content area down - a ground that started under the toolbar would draw a
 * seam across it.
 */
export function SettingsPane({
  toolbar,
  subtitle,
  children,
  className,
}: {
  /**
   * The pane's toolbar row, rendered on the ground but OUTSIDE the form: a window's title,
   * its back control and its one prominent action belong to the window frame, and the form
   * is what stands under them (the kit's Example Forms window, and every System Settings
   * pane). `.settings-pane-column` marks the form for exactly that reason - it sets no
   * width since S-C1 - and the pane's test reads it to tell the two apart.
   */
  readonly toolbar?: ReactNode | undefined;
  /**
   * THE GREY LINE UNDER THE TITLE, AND IT IS THE PANE'S RATHER THAN THE BAR'S (wave 5 round
   * 5, G5). `PageHeader` renders its own `subtitle` as a paragraph AFTER the `<header>`, so
   * on a pane it stood between the bar and the scrollport: the host pulls itself up by one
   * bar height, that line pushed it back down by its own 36, and rows then scrolled through
   * a paragraph that paints no ground (measured on the webhook detail pane at 1440: the host
   * began at y 36 with the bar at y 0). A line that does not have to stay put is CONTENT, so
   * it belongs inside the host and scrolls with the form. Panes take it here; `PageHeader`
   * keeps its own for the screens that are not panes.
   */
  readonly subtitle?: ReactNode | undefined;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}) {
  return (
    <div className={cx('settings-pane', className)}>
      {toolbar}
      {/*
        THE PANE IS THE SCREEN'S ONE SCROLLPORT, AND IT BEGINS AT THE BAR'S TOP EDGE (wave 5
        round 5, G5; owner decision C6-5, "the glass toolbar everywhere it belongs").

        WHY IT IS HERE AND NOT IN EIGHTEEN ROUTES. Every settings screen is this component
        with a different form in it, so the host is adopted ONCE and every pane - members,
        roles, role detail, objects and fields, identity, webhooks, workflows, AI assistant,
        operations, imports, exports, inbound - gets the same construction rather than
        eighteen hand-matched copies.

        WHAT IT CHANGES. Until now the DOCUMENT was the scroller on these screens: the bar
        is `md:sticky md:top-0`, so content did pass under it whenever the page happened to
        be taller than the window, and stood on a flat ground whenever it was not. A Mac
        window does not scroll its document - the kit instances `Scroll Edge Effect` inside
        `Windows/Dark/Unified Toolbar + Title + Sidebar/Active` over the RIGHT
        PANE's scroll view - so the pane under the bar owns the scrolling and the window
        holds still (`doc:` docs/decisions/ui-foundation-w5.md, A0; `wwdc:`
        https://developer.apple.com/videos/play/wwdc2026/102/ at 24:00).

        A0'S THREE RULES, ON THIS PANE. It is the one vertical scrollport (nothing inside a
        settings form declares `overflow-y` except a bounded LIST CONTROL - `OptionList` and
        `PickerList`, which are controls with a `max-height`, not scrollports); no pane pans
        horizontally; and no settings pane has a pinned first line, because the line that
        stays put here IS the window toolbar.
      */}
      <PaneHost host="settings">
        {subtitle !== undefined && <p className="py-2 text-sm text-ink-muted">{subtitle}</p>}
        <div className="settings-pane-column">{children}</div>
      </PaneHost>
    </div>
  );
}

export interface SettingsGroupProps {
  /** Bold title above the box. The FIRST group of a pane deliberately has none (P7-1). */
  readonly title?: string | undefined;
  /** One grey sentence under the box, in the person's words. Never inside the box. */
  readonly help?: ReactNode | undefined;
  /**
   * A bordered push button in the group's own footer row, for a group that CREATES its
   * rows ("New role..."). P7-3: no Apple reference image shows this row, so it is built
   * from the kit's group box and the kit's push button and captioned OPEN on the states
   * page.
   */
  readonly footer?: ReactNode | undefined;
  /** Names the list for a screen reader when the rows are a list of things. */
  readonly listLabel?: string | undefined;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

export function SettingsGroup({
  title,
  help,
  footer,
  listLabel,
  children,
  className,
}: SettingsGroupProps) {
  return (
    <section className={cx('settings-group', className)}>
      {title !== undefined && <h2 className="settings-group-title">{title}</h2>}
      <ul
        className="settings-group-box"
        {...(listLabel === undefined ? {} : { 'aria-label': listLabel })}
      >
        {children}
        {footer !== undefined && <li className="settings-group-footer">{footer}</li>}
      </ul>
      {help !== undefined && <p className="settings-group-help">{help}</p>}
    </section>
  );
}

/** The three row heights the kit's Forms master carries, floored at the pointer target. */
export type SettingsRowSize = 'ordinary' | 'control' | 'content';

export interface SettingsRowPresence {
  readonly present: boolean;
  readonly onExited: () => void;
}

/**
 * doc: docs/decisions/motion.md section 2, List row insert and remove.
 * Keep removed rows in their own slots until their fades settle. A filter or pane change
 * gives this component a different React key, so a different query swaps immediately.
 * The render callback adds no DOM: the SettingsRow remains the group's direct li child.
 */
export function SettingsRows<T>({
  items,
  getKey,
  children,
  empty = null,
  wrap,
}: {
  readonly items: readonly T[];
  readonly getKey: (item: T) => string;
  readonly children: (item: T, index: number, presence: SettingsRowPresence) => ReactNode;
  readonly empty?: ReactNode;
  /** Keep a group's disclosure or heading while its last retained row exits. */
  readonly wrap?: ((rows: ReactNode) => ReactNode) | undefined;
}) {
  const [previous, setPrevious] = useState(items);
  const [retained, setRetained] = useState(items);
  if (items.length !== previous.length || items.some((item, index) => item !== previous[index])) {
    const next = [...items];
    retained.forEach((item, index) => {
      if (!items.some((entry) => getKey(entry) === getKey(item))) next.splice(index, 0, item);
    });
    setPrevious(items);
    setRetained(next);
  }
  if (retained.length === 0) return empty;
  const rows = retained.map((item, index) => {
    const key = getKey(item);
    const currentIndex = items.findIndex((entry) => getKey(entry) === key);
    return (
      <Fragment key={key}>
        {children(item, currentIndex < 0 ? index : currentIndex, {
          present: currentIndex >= 0,
          onExited: () => setRetained((rows) => rows.filter((entry) => getKey(entry) !== key)),
        })}
      </Fragment>
    );
  });
  return wrap === undefined ? rows : wrap(rows);
}

export interface SettingsRowProps {
  /** The leading label. A string in almost every row; a node where badges sit beside it. */
  readonly label: ReactNode;
  /** The grey line under the label. */
  readonly description?: ReactNode | undefined;
  /** An avatar or a tile before the label. */
  readonly leading?: ReactNode | undefined;
  /** The trailing control: a switch, a pop-up button, or the value as text. */
  readonly control?: ReactNode | undefined;
  /** The row's More button, and it is the ONLY button a row may carry beside its control. */
  readonly actions?: ReactNode | undefined;
  /** Makes the whole row the link to a pushed pane, with the drill-in chevron. */
  readonly to?: string | undefined;
  /**
   * THE OTHER DRILL-IN (round 3). A settings row sometimes opens a SHEET rather than a
   * route - a rule that is edited in a dialog, a field whose form is a modal - and the
   * affordance a person reads is the same one: the whole row is the target and the chevron
   * says a tap goes deeper. `to` renders a `<Link>`, this renders a `<button>`, and both
   * carry the chevron; a row that reached for `onClick` on the `<li>` instead would be a
   * target no keyboard and no screen reader can find.
   */
  readonly onSelect?: (() => void) | undefined;
  /** The accessible name of that button, where the label alone does not say enough. */
  readonly selectLabel?: string | undefined;
  /**
   * Where `onSelect` REVEALS rows of the same box rather than opening a sheet: the button
   * carries `aria-expanded` and the chevron turns down, which is the disclosure the pane
   * already draws in `SettingsDisclosure` - the difference is only that the revealed rows
   * belong to this row rather than to the group.
   */
  readonly expanded?: boolean | undefined;
  readonly size?: SettingsRowSize | undefined;
  /** The destructive row's red label (spec section 2 item 7). */
  readonly tone?: 'default' | 'destructive' | undefined;
  readonly className?: string | undefined;
  readonly present?: boolean | undefined;
  readonly onExited?: (() => void) | undefined;
  /** Rendered under the label and the control, inside the row: a refusal, a reveal. */
  readonly children?: ReactNode | undefined;
}

export function SettingsRow({
  label,
  description,
  leading,
  control,
  actions,
  to,
  onSelect,
  selectLabel,
  expanded,
  size = 'ordinary',
  tone = 'default',
  className,
  present,
  onExited,
  children,
}: SettingsRowProps) {
  const motion = useMotion(present ?? true, { onExited });
  if (!motion.mounted) return null;
  const body = (
    <>
      {leading !== undefined && <span className="settings-row-leading">{leading}</span>}
      <span className="settings-row-text">
        <span className="settings-row-label">{label}</span>
        {description !== undefined && (
          <span className="settings-row-description">{description}</span>
        )}
      </span>
      {control !== undefined && <span className="settings-row-control">{control}</span>}
      {(to !== undefined || onSelect !== undefined) && (
        <Icon
          name="chevron-right"
          className={cx(
            'settings-row-chevron',
            expanded !== undefined && MOTION.turn,
            expanded === true && 'rotate-90',
          )}
        />
      )}
    </>
  );
  return (
    <li
      ref={motion.ref}
      className={cx('settings-row', present !== undefined && MOTION.row, className)}
      data-row={size}
      data-tone={tone}
      data-state={present === undefined ? undefined : motion.state}
      inert={present === false}
      aria-hidden={present === false ? true : undefined}
    >
      <div className="settings-row-line">
        {to !== undefined ? (
          <Link to={to} className="settings-row-main settings-row-link">
            {body}
          </Link>
        ) : onSelect !== undefined ? (
          <button
            type="button"
            onClick={onSelect}
            className="settings-row-main settings-row-link"
            {...(selectLabel === undefined ? {} : { 'aria-label': selectLabel })}
            {...(expanded === undefined ? {} : { 'aria-expanded': expanded })}
          >
            {body}
          </button>
        ) : (
          <div className="settings-row-main">{body}</div>
        )}
        {actions !== undefined && <div className="settings-row-actions">{actions}</div>}
      </div>
      {children}
    </li>
  );
}

/* --- THE ACTION ROW (wave 4, C3) ----------------------------------------------------
 *
 * THE ONE VARIANT OF `SettingsRow` THIS FILE CARRIES, and it exists because a Shortcuts
 * action row inverts the ordinary row's grammar. An ordinary settings row is a LABEL that
 * opens something, so the whole row is one control and the chevron rides inside it. An
 * action row is a SENTENCE whose parameters are themselves controls - Apple's "Get the
 * latest [5 photos]" - so the row cannot be a button: a button inside a button is invalid
 * markup and unreachable by keyboard, and the sentence would lose every parameter in it.
 *
 * THE WHOLE ROW IS THE ONE CONTROL, AND THE GATE IS WHAT DECIDED THAT. The first build made
 * each parameter its own `<button>` inside the sentence, which is what Apple's pills are - and
 * the measure step returned 45 blocking `touch-floor` findings, because a pill inside a 15px
 * line draws a 19 to 191 by 20 pixel hit rect against criterion 1's 40px floor, and `.hit-expand`
 * cannot help: grown to 40 in both directions, neighbouring tokens in one sentence would steal
 * each other's clicks. So the row is a button the way every other settings row is, the tokens
 * are SPANS inside it, and which token was hit is read off the click - the parameter still gets
 * the focus, and there is one tab stop per row instead of four 20px ones.
 *
 * THE CHEVRON RIDES INSIDE IT, and it is the row's only trailing affordance: "a row has no
 * three-dot menu at rest; remove and reorder live in the row's context menu and in the
 * disclosure" (`crm-design/spec/screens/settings-pane.md` part 2b item 2, from support:
 * https://support.apple.com/guide/shortcuts-mac/intro-to-shortcuts-apdf22b0444c/mac).
 * What it opens is rows of the SAME box - `.settings-row-reveal`, the construction round 3
 * already uses - because Apple does not draw a box inside a box (spec section 7) and the
 * wide Mac action card reveals its form under a hairline inside itself.
 *
 * Everything else is `SettingsRow`'s: the same `<li>`, the same hairline between rows, the
 * same leading slot, the same row heights.
 */
export interface SettingsActionRowProps {
  /** The leading tile. `ObjectTile` at every call site; the row only gives it its slot. */
  readonly leading?: ReactNode | undefined;
  /** The sentence, with its parameters inside it. Rendered as the row's label. */
  readonly sentence: ReactNode;
  /** The sentence in words, for the chevron's accessible name and for a test. */
  readonly plain: string;
  readonly expanded: boolean;
  /** The event comes with it, so a caller can read WHICH token in the sentence was hit. */
  readonly onExpandedChange: (next: boolean, event: MouseEvent<HTMLButtonElement>) => void;
  readonly size?: SettingsRowSize | undefined;
  /** Rendered under the row while it is open: the parameters as a label-and-control form. */
  readonly children?: ReactNode | undefined;
  readonly className?: string | undefined;
  /** The row element, so a caller can put the focus on the parameter a token names. */
  readonly rowRef?: Ref<HTMLLIElement> | undefined;
  /** Row presence is owned by the list, so removed rows can finish their fade. */
  readonly motionState?: MotionState | undefined;
}

export function SettingsActionRow({
  leading,
  sentence,
  plain,
  expanded,
  onExpandedChange,
  size = 'content',
  children,
  className,
  rowRef,
  motionState,
}: SettingsActionRowProps) {
  return (
    <li
      ref={rowRef}
      className={cx('settings-row', className)}
      data-row={size}
      data-tone="default"
      data-state={motionState}
      inert={motionState === 'closed'}
      aria-hidden={motionState === 'closed' ? true : undefined}
    >
      <div className="settings-row-line">
        <button
          type="button"
          aria-expanded={expanded}
          /* THE ACCESSIBLE NAME CONTAINS THE VISIBLE SENTENCE (WCAG 2.5.3): the row is the
             only thing a voice user can ask for by name, and "Show more" on three rows in a
             row names none of them. */
          aria-label={`${expanded ? 'Hide' : 'Show'} the settings of "${plain}"`}
          className="settings-row-main settings-row-link"
          onClick={(event) => onExpandedChange(!expanded, event)}
        >
          {leading !== undefined && <span className="settings-row-leading">{leading}</span>}
          <span className="settings-row-text">
            <span className="settings-row-label">{sentence}</span>
          </span>
          <Icon
            name="chevron-right"
            className={cx('settings-row-chevron', MOTION.turn, expanded && 'rotate-90')}
          />
        </button>
      </div>
      {expanded && children !== undefined && (
        <div className="settings-row-reveal settings-row-form">{children}</div>
      )}
    </li>
  );
}

/**
 * THE VALUE A ROW CARRIES AS TEXT (round 2, F1 and F5).
 *
 * Apple has NO TAG in a settings row. The kit's Forms row that carries a value carries it
 * as "Label / Label": 13pt at 50 percent ink on the trailing side, with nothing drawn
 * around it. A role, the kind of a principal, "You", "Suspended" and "Revoked" are all that
 * row - round 1 drew them as tinted and grey capsules, which is a web table's vocabulary
 * and the owner rejected it on 2026-09-10.
 */
export function SettingsValue({ children }: { readonly children: ReactNode }) {
  return <span className="settings-row-value">{children}</span>;
}

/**
 * THE POP-UP BUTTON'S TRIGGER (round 2, F6), for a menu whose items are not one-of-N and
 * therefore cannot be a `SelectMenu` - a principal holds N roles and they union (D2).
 *
 * WHAT THE KIT DRAWS: the Forms row's "Popup" trailing accessory is the chosen VALUE as
 * text followed by the arrow badge carrying the stacked up-and-down chevron.
 * kit: `Apple macOS 27 UI Kit.sketch / Arrow Buttons/Light/Content Area/3 Rg/1 - Idle`, frame 24x24 and black fill alpha 0.08.
 * kit: `Apple macOS 27 UI Kit.sketch / Arrow Buttons/Dark/Content Area/3 Rg/1 - Idle`, frame 24x24 and white fill alpha 0.07.
 * There is no border and no capsule around the value; that look belongs to a
 * pull-down, which is the control round 1 rendered here.
 *
 * THE BADGE'S GLYPH IS TWO EXISTING CHEVRONS, and that is stated rather than hidden: the
 * kit's badge carries SF Symbols' `chevron.up.chevron.down`, `ui/Icon` has no such glyph,
 * and a builder does not draw one (owner's rule, 2026-09-10). Stacking `chevron-up` over
 * `chevron-down` uses only glyphs the product already ships; the states page captions it
 * OPEN so the owner can call for the real symbol as a design step.
 *
 * `...rest` reaches the `<button>` because Radix's `asChild` clones this element with the
 * menu's own props and ref on it.
 */
export interface SettingsPopUpProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  /**
   * The CHOSEN value, which is what a pop-up shows - never a verb (rulebook D12). It
   * shadows the DOM's own `value`, which a `<button>` has no use for.
   */
  readonly value: ReactNode;
}

/**
 * What the trigger DRAWS, shared with `SelectMenu`'s `popup` variant so the one-of-N
 * control and the N-of-N one are the same object on screen rather than two hand-matched
 * ones. Radix owns the `<button>` in that case, so only the contents are shared.
 */
export function SettingsPopUpBody({ value }: { readonly value: ReactNode }) {
  return (
    <>
      <span className="settings-popup-value">{value}</span>
      <span className="settings-popup-badge" aria-hidden="true">
        <Icon name="chevron-up" size="0.5rem" />
        <Icon name="chevron-down" size="0.5rem" />
      </span>
      <Icon name="chevron-right" className="settings-popup-chevron" size="0.875rem" />
    </>
  );
}

export function SettingsPopUp({ value, className, type = 'button', ...rest }: SettingsPopUpProps) {
  return (
    <button type={type} className={cx('settings-popup', className)} {...rest}>
      <SettingsPopUpBody value={value} />
    </button>
  );
}

/**
 * THE ROW'S MORE BUTTON (round 2, F10), and it is DRAWN AT IDLE.
 *
 * The kit's Forms page has a row called "More button": its trailing slot holds the ellipsis
 * glyph at 13pt and 50 percent ink, with no button behind it. Round 1 used the product's
 * `IconButton`, whose plain variant faded the glyph to the point where the review capture
 * showed three barely-there dots; this is the kit's weight, and it is the same reason the
 * row's menu has to be reachable with a thumb, where there is no hover to reveal anything.
 */
export function SettingsMoreButton({
  label,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { readonly label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className="settings-row-more hit-expand"
      {...rest}
    >
      <Icon name="more-horizontal" size="1.0625rem" />
    </button>
  );
}

/**
 * P7-3, the one construction no Apple reference image shows. The kit gives the group box
 * (`Group Boxes/Light`) and the push button (`Content Area/Light/01 - Bordered/01 - Idle`);
 * what it does not give is a picture of the two together, so the placement - a row of its
 * own at the bottom of the box, leading-aligned - is OURS and is captioned OPEN on
 * `/dev/settings-states` rather than passed off as measured.
 */
export function SettingsFooterButton({
  children,
  onClick,
  disabled,
}: {
  readonly children: ReactNode;
  readonly onClick: () => void;
  readonly disabled?: boolean | undefined;
}) {
  return (
    <Button variant="secondary" size="sm" onClick={onClick} disabled={disabled}>
      {children}
    </Button>
  );
}

/**
 * "For developers", last, collapsed (spec section 2 item 6). A disclosure inside a settings
 * pane is a GROUP whose first row opens the rest - Apple does not draw a box inside a box,
 * so the revealed rows are rows of the same box rather than a nested surface.
 *
 * CONTROLLED, like `CollapsibleSection`: two copies of one disclosure's state is how a pane
 * ends up with a chevron pointing the wrong way.
 *
 * THE REVEALED ROWS ARE ROWS OF THE SAME BOX, which is why this does not reach for the
 * Radix collapsible the way `CollapsibleSection` does: that primitive owns a wrapper
 * element, and a wrapper between the `<ul>` and its rows would either break the list
 * semantics the panes' tests read or nest a second list inside one row. The trigger is a
 * real button carrying `aria-expanded`, and the rows are rendered or they are not.
 */
export interface SettingsDisclosureProps {
  readonly title: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Shown in grey after the title, closed and open alike: a count, a reason. */
  readonly meta?: ReactNode | undefined;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

export function SettingsDisclosure({
  title,
  open,
  onOpenChange,
  meta,
  children,
  className,
}: SettingsDisclosureProps) {
  return (
    <section className={cx('settings-group settings-disclosure', className)}>
      <ul className="settings-group-box">
        <li className="settings-row" data-row="ordinary">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => onOpenChange(!open)}
            className="settings-disclosure-trigger"
          >
            <Icon
              name="chevron-right"
              className={cx('settings-disclosure-chevron', MOTION.turn, open && 'rotate-90')}
            />
            <span className="settings-row-label">{title}</span>
            {meta !== undefined && <span className="settings-row-description">{meta}</span>}
          </button>
        </li>
        {open && children}
      </ul>
    </section>
  );
}

/**
 * The destructive action, last and separated, as its own group with one red row (spec
 * section 2 item 7 and section 7: Apple never puts it at the top of a pane or in the
 * toolbar). The alert belongs to the caller - only the irreversible case gets one.
 */
export function SettingsDestructiveRow({
  label,
  description,
  onClick,
  disabled,
}: {
  readonly label: string;
  readonly description?: ReactNode | undefined;
  readonly onClick: () => void;
  readonly disabled?: boolean | undefined;
}) {
  return (
    <SettingsGroup>
      <SettingsRow
        tone="destructive"
        label={
          <button
            type="button"
            className="settings-destructive-button"
            onClick={onClick}
            disabled={disabled}
          >
            {label}
          </button>
        }
        {...(description === undefined ? {} : { description })}
      />
    </SettingsGroup>
  );
}
